import { useState, useEffect, useCallback } from 'react';
import { List } from '../types/lists';
import { 
  ScheduleRecommendation, 
  InAppNotification,
  CompletionEvent 
} from '../types/intelligentScheduling';
import { basicIntelligentScheduler } from '../services/intelligentScheduling/BasicIntelligentScheduler';
import { contextAnalysisService } from '../services/intelligentScheduling/ContextAnalysisService';
import { smartNotificationService } from '../services/intelligentScheduling/SmartNotificationService';

/**
 * Hook for managing intelligent scheduling functionality
 */
export function useIntelligentScheduling() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [recommendations, setRecommendations] = useState<ScheduleRecommendation[]>([]);
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      // Load notifications
      const activeNotifications = smartNotificationService.getActiveNotifications();
      setNotifications(activeNotifications);

      // Load analytics
      const analyticsData = contextAnalysisService.getProductivityAnalytics();
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Failed to load initial scheduling data:', error);
    }
  };

  /**
   * Generates intelligent schedule recommendations for a list of tasks
   */
  const generateIntelligentSchedules = useCallback(async (
    lists: List[], 
    constraints?: {
      maxDailyHours?: number;
      preferredTimeBlocks?: string[];
      avoidTimeBlocks?: string[];
    }
  ): Promise<ScheduleRecommendation[]> => {
    setIsGenerating(true);
    
    try {
      console.log('🧠 Generating intelligent schedules for', lists.length, 'lists');
      
      const newRecommendations = await basicIntelligentScheduler.generateOptimalSchedule(
        lists, 
        undefined, // Let the service gather context
        constraints
      );
      
      setRecommendations(newRecommendations);
      
      // Generate notifications for high-confidence recommendations
      await generateScheduleNotifications(newRecommendations);
      
      return newRecommendations;
    } catch (error) {
      console.error('Failed to generate intelligent schedules:', error);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  /**
   * Generates notifications for schedule recommendations
   */
  const generateScheduleNotifications = async (recommendations: ScheduleRecommendation[]) => {
    for (const rec of recommendations) {
      // Only notify for high-confidence recommendations
      if (rec.confidence < 0.7) continue;

      try {
        const message = generateNotificationMessage(rec);
        await smartNotificationService.sendScheduleNotification(
          `schedule_${rec.listId}_${Date.now()}`,
          rec.listId,
          `Time for: ${getListTitle(rec.listId)}`,
          message,
          'schedule_reminder',
          {
            priority: rec.confidence > 0.85 ? 'high' : 'medium',
            confidence: rec.confidence,
            contextFactors: rec.reasoning
          }
        );
      } catch (error) {
        console.error('Failed to send schedule notification:', error);
      }
    }
  };

  /**
   * Records a task completion event for learning
   */
  const recordCompletionEvent = useCallback(async (
    listId: string,
    duration: number,
    quality: 1 | 2 | 3 | 4 | 5,
    context?: {
      wasScheduled?: boolean;
      distractionLevel?: 1 | 2 | 3 | 4 | 5;
      energyLevel?: 1 | 2 | 3 | 4 | 5;
    }
  ) => {
    const event: CompletionEvent = {
      id: `completion_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      listId,
      completedAt: new Date(),
      duration,
      quality,
      context: {
        timeOfDay: new Date().getHours(),
        dayOfWeek: new Date().getDay(),
        wasScheduled: context?.wasScheduled || false,
        distractionLevel: context?.distractionLevel || 3,
        energyLevel: context?.energyLevel || 3
      }
    };

    contextAnalysisService.recordCompletionEvent(event);
    
    // Refresh analytics
    const updatedAnalytics = contextAnalysisService.getProductivityAnalytics();
    setAnalytics(updatedAnalytics);
    
    console.log('📊 Completion event recorded for learning');
  }, []);

  /**
   * Gets scheduling suggestions for a specific list
   */
  const getSchedulingSuggestion = useCallback(async (list: List): Promise<{
    recommended: boolean;
    timeSlot?: {
      startTime: Date;
      duration: number;
      confidence: number;
    };
    reasoning: string[];
  }> => {
    try {
      const recommendations = await basicIntelligentScheduler.generateOptimalSchedule([list]);
      
      if (recommendations.length === 0) {
        return {
          recommended: false,
          reasoning: ['Unable to generate recommendation']
        };
      }

      const rec = recommendations[0];
      return {
        recommended: rec.confidence > 0.6,
        timeSlot: {
          startTime: rec.primaryRecommendation.startTime,
          duration: rec.primaryRecommendation.optimalDuration,
          confidence: rec.confidence
        },
        reasoning: rec.reasoning
      };
    } catch (error) {
      console.error('Failed to get scheduling suggestion:', error);
      return {
        recommended: false,
        reasoning: ['Error generating suggestion']
      };
    }
  }, []);

  /**
   * Gets the user's productivity insights
   */
  const getProductivityInsights = useCallback(() => {
    return {
      analytics,
      peakHours: analytics?.peakHours || [],
      preferredWorkStyle: analytics?.preferredWorkStyle || [],
      completionTrend: analytics?.completionTrend || 'stable',
      recommendations: generateProductivityRecommendations(analytics)
    };
  }, [analytics]);

  return {
    // State
    isGenerating,
    recommendations,
    notifications,
    analytics,
    
    // Actions
    generateIntelligentSchedules,
    recordCompletionEvent,
    getSchedulingSuggestion,
    getProductivityInsights,
    
    // Utils
    refreshData: loadInitialData
  };
}

// Helper functions

function generateNotificationMessage(recommendation: ScheduleRecommendation): string {
  const confidence = Math.round(recommendation.confidence * 100);
  let message = `Based on your patterns, now is a good time (${confidence}% confidence).`;
  
  if (recommendation.reasoning.includes('peak_productivity')) {
    message += ' You\'re typically most productive at this time.';
  }
  if (recommendation.reasoning.includes('deadline_approaching')) {
    message += ' Deadline is approaching.';
  }
  if (recommendation.reasoning.includes('historical_success')) {
    message += ' You complete similar tasks well at this time.';
  }
  
  return message;
}

function getListTitle(_listId: string): string {
  // This would need to integrate with the actual lists store
  // For now, return a placeholder
  return 'Your Task';
}

function generateProductivityRecommendations(analytics: any): string[] {
  const recommendations: string[] = [];
  
  if (!analytics) return recommendations;
  
  if (analytics.averageQuality < 3) {
    recommendations.push('Consider breaking tasks into smaller chunks');
  }
  
  if (analytics.completionTrend === 'declining') {
    recommendations.push('You might benefit from more breaks and shorter work sessions');
  }
  
  if (analytics.peakHours.length > 0) {
    const peakHoursStr = analytics.peakHours.join(', ');
    recommendations.push(`Your peak productivity hours are ${peakHoursStr} - schedule important tasks then`);
  }
  
  return recommendations;
}

/**
 * Hook specifically for notification management
 */
export function useNotifications() {
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    // Load initial notifications
    const loadNotifications = () => {
      const activeNotifications = smartNotificationService.getActiveNotifications();
      setNotifications(activeNotifications);
      setUnreadCount(activeNotifications.filter(n => !n.isRead).length);
    };

    loadNotifications();

    // Listen for updates
    const handleNotificationUpdate = async (event: string, data: any) => {
      switch (event) {
        case 'notification_added':
          setNotifications(prev => [data, ...prev]);
          setUnreadCount(prev => prev + 1);
          break;
        case 'notification_updated':
          setNotifications(prev => prev.map(n => n.id === data.id ? data : n));
          if (data.isRead) {
            setUnreadCount(prev => Math.max(0, prev - 1));
          }
          break;
        case 'notification_removed': {
          setNotifications(prev => {
            const remaining = prev.filter(n => n.id !== data.notificationId);
            setUnreadCount(remaining.filter(n => !n.isRead).length);
            return remaining;
          });
          break;
        }
        case 'complete_next_item': {
          // Handle completing the next item in the specified list
          try {
            const { listsService } = await import('../services/listsService');
            const result = await listsService.completeNextItemAndGetNext(data.listId);
            
            if (result.completedItem) {
              console.log('✅ Completed item via notification:', result.completedItem.content);
              
              // You could trigger a UI refresh here if needed
              // For example, emit an event that the lists page can listen to
              window.dispatchEvent(new CustomEvent('itemCompleted', {
                detail: {
                  listId: data.listId,
                  completedItem: result.completedItem,
                  nextItem: result.nextItem
                }
              }));
            } else {
              console.log('ℹ️ No incomplete items found in list:', data.listId);
            }
          } catch (error) {
            console.error('Failed to complete next item:', error);
          }
          break;
        }
      }
    };

    smartNotificationService.addNotificationListener(handleNotificationUpdate);

    return () => {
      smartNotificationService.removeNotificationListener(handleNotificationUpdate);
    };
  }, []); // Empty dependency array since we don't want to re-run this effect

  const handleNotificationAction = useCallback(async (
    notificationId: string, 
    action: string, 
    data?: any
  ) => {
    try {
      await smartNotificationService.handleNotificationAction(notificationId, action, data);
    } catch (error) {
      console.error('Failed to handle notification action:', error);
    }
  }, []);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await smartNotificationService.markAsRead(notificationId);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, []);

  return {
    notifications,
    unreadCount,
    handleNotificationAction,
    markAsRead
  };
}